/* eslint-disable import/no-anonymous-default-export */
import React, { useState, useEffect, useCallback } from 'react';
import { List, ListItem, Divider, Drawer, Icon } from '@mui/material';
import { get, map, size } from 'global/lib/lodash';
import useStyles from './styles';
import Skeleton from '@mui/material/Skeleton';
import { useScreen } from 'global/styles';
import { useLocation } from 'react-router-dom';
import { getId } from './index';
import clsx from 'clsx';
import { numString } from '../../../global/utils';
import { displayText } from '../../../global/components/ScoreIcon';

const SideBarList = (props) => {
  const { isCurrent, pssvSystem, setSidebarOpen, hash, setHash, setActiveEdit, activeEdit, canShowScores, filter } = props;
  const [open, setOpen] = useState(isCurrent);
  const classes = useStyles();

  const toggleOpen = (bool) => {
    setOpen(bool);
  };

  useEffect(() => {
    toggleOpen(isCurrent);
  }, [isCurrent]);

  const onClick = useCallback(
    (c) => {
      setHash(getId(c));
      if (activeEdit && getId(get(activeEdit, 'pssvChecklist')) !== getId(c)) {
        setActiveEdit({
          pssvChecklist: c,
          pssvSystem,
          scroll: true,
          scrollBy: 48,
        });
      }
    },
    [activeEdit]
  );

  const useCompareScore = get(filter, 'assessment_status', 'active') !== 'active';
  const useDiff = get(filter, 'useDiff', false);
  let sysVal = useCompareScore ? get(pssvSystem, 'compare.displayValue') : pssvSystem.displayValue;
  if (useCompareScore && useDiff) {
    sysVal = displayText(pssvSystem, pssvSystem.compare, props);;
  }

  return (
    <>
      <ListItem button className={isCurrent ? classes.systemActive : classes.systemInactive} onClick={() => toggleOpen(!open)}>
        <div className={classes.listItemWrapper}>
          <span className={classes.headerChild}>{pssvSystem.name}</span>
          {canShowScores && <span className={clsx(classes.headerChild, classes.sidebarHeaderChild)}>{sysVal}</span>}
          <Icon className={classes.expandIcon}>{open ? 'expand_less' : 'expand_more'}</Icon>
        </div>
      </ListItem>
      <div className={open ? classes.show : classes.hide} onClick={() => setSidebarOpen(false)}>
        {get(props, 'pssvChecklists', []).map((pssvChecklist) => {
          const id = getId(pssvChecklist).toString();
          let checkVal = useCompareScore ? get(pssvChecklist, 'compare.displayValue') : pssvChecklist.displayValue;
          if (useCompareScore && useDiff) {
            checkVal = displayText(pssvChecklist, pssvChecklist.compare, props);
          }
          return (
            <ListItem
              key={id}
              button={true}
              onClick={() => onClick(pssvChecklist)}
              className={clsx(classes.listItemWrapper, classes.listItemWrapChild, hash === id ? classes.checklistActive : classes.checklistInactive)}
            >
              <span className={classes.checklistMenuHeader}>{pssvChecklist.name}</span>
              {canShowScores && <span className={classes.checklistMenuHeader}>{checkVal}</span>}
            </ListItem>
          );
        })}
      </div>
      <Divider />
    </>
  );
};

export default (props) => {
  const { pssvSystems, processedOverallScores, loading, open, setSidebarOpen, setActiveEdit, activeEdit, canShowScores, scorecardIndex, filter } = props;
  const location = useLocation();
  const { isMobile } = useScreen();
  const classes = useStyles();
  const [hash, setHash] = useState();

  const DRAW = isMobile ? Drawer : 'div';

  useEffect(() => {
    // This triggers whenever a user actively changes something in the interface,
    // whether it's clicking "Edit" or it's clicking on a sidebar to navigate
    // (If a user is just scrolling, this if statement won't ever evaluate to true.)
    if (hash && hash !== get(location, 'state.id', '')) {
      location.state = { id: hash };
      if (!activeEdit) {
        // triggers when a user clicks on the sidebar menu item while not in Edit mode
        document.getElementById(hash).scrollIntoView(true);
        window.scrollBy(0, -48);
      }
    }
  }, [hash]);

  useEffect(() => {
    try {
      if (activeEdit) {
        // triggers when user enters Edit Mode
        setHash(getId(get(activeEdit, 'pssvChecklist')));
      } else if (hash) {
        // triggers when user exits Edit Mode: scroll to the item just edited
        document.getElementById(hash).scrollIntoView(true);
        window.scrollBy(0, -48);
      }
    } catch (err) {
      console.error(err);
    }
  }, [activeEdit]);

  const handleScroll = () => {
    setHash(get(location, 'state.id', ''));
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const useCompareScore = get(filter, 'assessment_status', 'active') !== 'active';
  const useDiff = get(filter, 'useDiff', false);
  let overall = get(processedOverallScores, `${scorecardIndex}.${useCompareScore ? 'compare.displayValue' : 'displayValue'}`)
  if (useCompareScore && useDiff) {
    const thisscore = get(processedOverallScores, scorecardIndex);
    const comp = get(thisscore, 'compare');
    overall = displayText(thisscore, comp, props);;
  }

  return (
    <DRAW className={isMobile ? classes.leftSidebarMobile : classes.leftSidebar} open={open} onClose={() => setSidebarOpen(false)}>
      {(loading || !size(pssvSystems)) && <Skeleton variant="rectangular" height={2000} />}
      <List>
        <ListItem className={classes.overallScore}>{`Overall:`}<span style={{ marginLeft: 2 }}>{overall}</span></ListItem>
        {map(pssvSystems, (sys, i) => {
          const isCurrent = hash && (getId(sys).toString() === hash || get(sys, 'children', []).find((ps) => getId(ps).toString() === hash));
          return (
            <SideBarList
              key={i}
              filter={filter}
              pssvSystem={sys}
              pssvChecklists={sys.children}
              setSidebarOpen={setSidebarOpen}
              isCurrent={isCurrent}
              hash={hash}
              setHash={setHash}
              setActiveEdit={setActiveEdit}
              activeEdit={activeEdit}
              canShowScores={canShowScores}
              scorecard={props.scorecard}
            />
          );
        })}
      </List>
    </DRAW>
  );
};
